// TypeScript types for teams functionality

export interface Team {
  id: string;
  name: string;
  description?: string;
  owner_id: string;
  settings?: string; // JSON
  is_active: boolean;
  created_at: string;
  updated_at: string;
  member_count?: number;
  user_role?: TeamRole;
}

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: TeamRole;
  permissions?: string; // JSON
  invited_by: string;
  joined_at: string;
  last_active_at: string;
  is_active: boolean;
  user_email?: string;
  user_name?: string;
}

export interface TeamInvitation {
  id: string;
  team_id: string;
  email: string;
  role: Exclude<TeamRole, 'owner'>;
  invited_by: string;
  invitation_token: string;
  message?: string;
  expires_at: string;
  accepted_at?: string;
  declined_at?: string;
  created_at: string;
  team_name?: string;
  inviter_email?: string;
}

export interface TeamActivityLog {
  id: string;
  team_id: string;
  user_id: string;
  action_type: TeamActionType;
  action_details?: string; // JSON
  target_user_id?: string;
  created_at: string;
  user_email?: string;
  target_user_email?: string;
}

export type TeamRole = 'owner' | 'admin' | 'member' | 'viewer';

export type TeamPermission = 
  | 'view' 
  | 'edit' 
  | 'admin' 
  | 'delete_team' 
  | 'manage_members' 
  | 'manage_settings';

export type TeamActionType = 
  | 'team_created'
  | 'member_added'
  | 'member_removed'
  | 'role_changed'
  | 'team_updated'
  | 'team_deleted';

export interface CreateTeamRequest {
  name: string;
  description?: string;
}

export interface InviteToTeamRequest {
  email: string;
  role: Exclude<TeamRole, 'owner'>;
  message?: string;
}

export interface UpdateMemberRoleRequest {
  userId: string;
  role: Exclude<TeamRole, 'owner'>;
}

// API Response types
export interface TeamsListResponse {
  success: boolean;
  teams: Team[];
  error?: string;
}

export interface TeamResponse {
  success: boolean;
  team?: Team;
  error?: string;
}

export interface TeamMembersResponse {
  success: boolean;
  members?: TeamMember[];
  error?: string;
}

export interface TeamInvitationResponse {
  success: boolean;
  invitation?: TeamInvitation;
  message?: string;
  error?: string;
}

export interface DeleteTeamResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface RemoveMemberResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// Team context types
export interface TeamContextType {
  // Current state
  currentTeam: Team | null;
  userTeams: Team[];
  isPersonalMode: boolean;
  loading: boolean;
  error: string | null;

  // Actions
  loadTeams: () => Promise<void>;
  switchToTeam: (teamId: string) => void;
  switchToPersonal: () => void;
  createTeam: (data: CreateTeamRequest) => Promise<Team | null>;
  inviteToTeam: (teamId: string, data: InviteToTeamRequest) => Promise<boolean>;
  deleteTeam: (teamId: string) => Promise<boolean>;
  removeMember: (teamId: string, memberId: string) => Promise<boolean>;
  
  // Utilities
  hasPermission: (permission: TeamPermission) => boolean;
  getUserRole: () => TeamRole | null;
  isTeamOwner: () => boolean;
  isTeamAdmin: () => boolean;
}

// Permission definitions
export const TEAM_PERMISSIONS: Record<TeamRole, TeamPermission[]> = {
  owner: ['view', 'edit', 'admin', 'delete_team', 'manage_members', 'manage_settings'],
  admin: ['view', 'edit', 'admin', 'manage_members', 'manage_settings'],
  member: ['view', 'edit'],
  viewer: ['view']
};

// Role display names
export const ROLE_DISPLAY_NAMES: Record<TeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer'
};

// Role descriptions
export const ROLE_DESCRIPTIONS: Record<TeamRole, string> = {
  owner: 'Full control over the team, including deletion',
  admin: 'Can manage team members and settings',
  member: 'Can view and edit team content',
  viewer: 'Read-only access to team content'
};

// Utility functions
export const hasTeamPermission = (role: TeamRole | null, permission: TeamPermission): boolean => {
  if (!role) return false;
  return TEAM_PERMISSIONS[role].includes(permission);
};

export const canManageMembers = (role: TeamRole | null): boolean => {
  return hasTeamPermission(role, 'manage_members');
};

export const canEditContent = (role: TeamRole | null): boolean => {
  return hasTeamPermission(role, 'edit');
};

export const canViewContent = (role: TeamRole | null): boolean => {
  return hasTeamPermission(role, 'view');
}; 